import React, { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

/* ------------------------------------------------------------------ */
/* Shared layout — warm palette, rounded corners, ~700px prose width  */
/* ------------------------------------------------------------------ */
function LegalDocLayout({ title, children, testid }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const returnTo = (state && state.returnTo) || "/";

  useEffect(() => {
    // Ensure we start at the top of the document when navigating in
    window.scrollTo({ top: 0 });
  }, []);

  const handleBack = () => {
    // Prefer browser history (keeps the chat state intact), else fall back to returnTo
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate(returnTo);
    }
  };

  return (
    <div
      data-testid={testid}
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 700px at 50% -10%, #22180a 0%, #0b0804 60%, #000 100%)",
        color: "#f3e6cc",
        padding: "48px 20px 80px",
        fontFamily:
          "system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
      }}
    >
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 28,
          }}
        >
          <button
            type="button"
            onClick={handleBack}
            data-testid="legal-doc-back"
            style={{
              padding: "9px 16px",
              borderRadius: 999,
              border: "1px solid rgba(215, 180, 120, 0.35)",
              background: "rgba(80, 55, 25, 0.45)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              color: "#f3e6cc",
              fontSize: 13,
              fontWeight: 500,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            ← Volver
          </button>
          <span
            style={{
              fontSize: 12,
              letterSpacing: "0.2em",
              color: "rgba(215, 180, 120, 0.85)",
              fontWeight: 600,
            }}
          >
            PRELEGAL.CL
          </span>
        </div>

        <h1
          style={{
            fontSize: 34,
            fontWeight: 700,
            margin: "0 0 8px",
            letterSpacing: "-0.015em",
            color: "#fff8eb",
          }}
        >
          {title}
        </h1>
        <p
          style={{
            color: "rgba(243, 230, 204, 0.55)",
            fontSize: 13,
            margin: "0 0 32px",
          }}
        >
          Última actualización: febrero 2026
        </p>

        <article
          data-testid="legal-doc-content"
          style={{
            background: "rgba(35, 24, 12, 0.55)",
            border: "1px solid rgba(215, 180, 120, 0.25)",
            backdropFilter: "blur(18px) saturate(140%)",
            WebkitBackdropFilter: "blur(18px) saturate(140%)",
            borderRadius: 18,
            padding: "32px 36px",
            boxShadow: "0 18px 50px rgba(0,0,0,0.45)",
            lineHeight: 1.7,
            fontSize: 15,
            color: "#f3e6cc",
          }}
        >
          {children}
        </article>

        <div
          style={{
            marginTop: 28,
            display: "flex",
            gap: 16,
            flexWrap: "wrap",
            fontSize: 13,
            color: "rgba(215, 180, 120, 0.65)",
          }}
        >
          <Link style={footerLinkStyle} to="/prelegal/terminos">Términos y Condiciones</Link>
          <span style={{ opacity: 0.4 }}>·</span>
          <Link style={footerLinkStyle} to="/prelegal/privacidad">Política de Privacidad</Link>
          <span style={{ opacity: 0.4 }}>·</span>
          <Link style={footerLinkStyle} to="/prelegal/aviso-legal">Aviso Legal</Link>
        </div>
      </div>
    </div>
  );
}

const footerLinkStyle = {
  color: "rgba(243, 230, 204, 0.85)",
  textDecoration: "none",
  borderBottom: "1px dotted rgba(243, 230, 204, 0.35)",
  paddingBottom: 1,
};

const H2 = ({ children }) => (
  <h2
    style={{
      color: "#fff8eb",
      fontSize: 18,
      fontWeight: 600,
      margin: "28px 0 10px",
      letterSpacing: "-0.01em",
    }}
  >
    {children}
  </h2>
);

const P = ({ children }) => (
  <p style={{ margin: "0 0 12px" }}>{children}</p>
);

/* ------------------------------------------------------------------ */
/*  AVISO LEGAL (user-provided text)                                  */
/* ------------------------------------------------------------------ */
export function PreLegalAvisoLegalPage() {
  return (
    <LegalDocLayout title="Aviso Legal" testid="prelegal-aviso-page">
      <P>
        <strong>PreLegal.cl</strong> es una plataforma tecnológica de asistencia legal que
        entrega orientación general basada en la información proporcionada por el usuario.
      </P>
      <P>
        La información, análisis y documentos generados por el sistema tienen carácter
        informativo y referencial, y no constituyen asesoría jurídica profesional ni
        reemplazan la consulta con un abogado habilitado.
      </P>
      <P>El uso de esta plataforma no crea una relación abogado-cliente.</P>
      <P>
        Si bien el sistema está diseñado para apoyar la comprensión de situaciones legales y
        facilitar la preparación de antecedentes, se recomienda siempre validar la
        información con un profesional del derecho antes de tomar decisiones o iniciar
        acciones legales.
      </P>
      <P>
        <strong>PreLegal.cl</strong> no se hace responsable por decisiones adoptadas
        exclusivamente en base a la información entregada por la plataforma.
      </P>
    </LegalDocLayout>
  );
}

/* ------------------------------------------------------------------ */
/*  TÉRMINOS Y CONDICIONES                                            */
/* ------------------------------------------------------------------ */
export function PreLegalTermsPage() {
  return (
    <LegalDocLayout title="Términos y Condiciones de Uso" testid="prelegal-terms-page">
      <P>
        Los presentes Términos y Condiciones regulan el uso de la plataforma{" "}
        <strong>PreLegal.cl</strong> (en adelante, "la Plataforma"). Al acceder a ella, el
        usuario acepta quedar vinculado por los presentes términos.
      </P>

      <H2>1. Descripción del servicio</H2>
      <P>
        PreLegal.cl es una plataforma de asistencia legal basada en inteligencia artificial
        que entrega orientación general sobre situaciones jurídicas en el marco del derecho
        chileno, permite analizar documentos aportados por el usuario y facilita la
        preparación de borradores y antecedentes legales.
      </P>
      <P>
        La información entregada tiene carácter informativo y referencial. No constituye
        asesoría jurídica profesional ni reemplaza la consulta con un abogado habilitado.
        El uso de la Plataforma no crea una relación abogado-cliente.
      </P>

      <H2>2. Registro y acceso</H2>
      <P>
        Algunas funcionalidades requieren un acceso privado mediante contraseña entregada
        por PreLegal.cl. El usuario es responsable de mantener la confidencialidad de
        dicha contraseña y de toda la actividad que se realice bajo su acceso.
      </P>

      <H2>3. Uso permitido</H2>
      <P>El usuario se compromete a utilizar la Plataforma:</P>
      <ul style={{ paddingLeft: 22, margin: "0 0 12px" }}>
        <li>Con fines lícitos y conforme a la normativa chilena vigente.</li>
        <li>
          Sin intentar vulnerar su seguridad, suplantar identidades o abusar de los
          límites técnicos del servicio.
        </li>
        <li>
          Sin utilizarla para generar, distribuir o difundir contenido que infrinja
          derechos de terceros o la legislación aplicable.
        </li>
      </ul>

      <H2>4. Propiedad intelectual</H2>
      <P>
        Todo el contenido, software, diseño, marcas y elementos visuales de la Plataforma
        son propiedad de PreLegal.cl o de sus licenciantes y están protegidos por las
        normas de propiedad intelectual chilenas e internacionales.
      </P>
      <P>
        Los documentos, consultas y respuestas generadas en el marco de una sesión del
        usuario pertenecen a dicho usuario, sin perjuicio del uso interno agregado y
        anonimizado que PreLegal.cl pueda realizar para mejorar el servicio.
      </P>

      <H2>5. Limitación de responsabilidad</H2>
      <P>
        La Plataforma se entrega "tal cual", sin garantías expresas o implícitas sobre
        exactitud, completitud o adecuación a un propósito particular. PreLegal.cl no
        responde por decisiones adoptadas exclusivamente en base a la información
        entregada por el sistema.
      </P>
      <P>
        En ningún caso PreLegal.cl será responsable por daños indirectos, lucro cesante o
        perjuicios derivados del uso o imposibilidad de uso de la Plataforma.
      </P>

      <H2>6. Disponibilidad y cambios</H2>
      <P>
        PreLegal.cl podrá modificar, suspender o discontinuar cualquier funcionalidad en
        cualquier momento. También podrá actualizar estos Términos, publicando la nueva
        versión en esta misma página. El uso continuado después de dichos cambios implica
        su aceptación.
      </P>

      <H2>7. Legislación aplicable</H2>
      <P>
        Estos Términos se rigen por la legislación chilena. Cualquier controversia se
        someterá a los tribunales ordinarios de la ciudad de Santiago de Chile.
      </P>

      <H2>8. Contacto</H2>
      <P>
        Para consultas, reclamos o solicitudes relacionadas con estos Términos, el usuario
        puede escribir al correo indicado en la página principal de PreLegal.cl.
      </P>
    </LegalDocLayout>
  );
}

/* ------------------------------------------------------------------ */
/*  POLÍTICA DE PRIVACIDAD                                            */
/* ------------------------------------------------------------------ */
export function PreLegalPrivacyPage() {
  return (
    <LegalDocLayout title="Política de Privacidad" testid="prelegal-privacy-page">
      <P>
        En <strong>PreLegal.cl</strong> valoramos la privacidad de nuestros usuarios. La
        presente Política describe qué datos recopilamos, con qué finalidad los tratamos y
        los derechos que asisten al usuario, conforme a la Ley N° 19.628 sobre Protección
        de la Vida Privada y demás normativa chilena aplicable.
      </P>

      <H2>1. Datos que recopilamos</H2>
      <P>Durante el uso de la Plataforma podemos recopilar:</P>
      <ul style={{ paddingLeft: 22, margin: "0 0 12px" }}>
        <li>
          <strong>Contenido de las consultas y documentos</strong> que el usuario envía
          (textos, archivos PDF, Word, Excel, imágenes).
        </li>
        <li>
          <strong>Datos técnicos</strong> mínimos del navegador y conexión necesarios para
          la operación del servicio (agente de usuario, dirección IP, logs de acceso).
        </li>
        <li>
          <strong>Identificadores de sesión</strong> y metadatos de las conversaciones
          guardadas (título, fecha, modo de consulta).
        </li>
      </ul>
      <P>
        No solicitamos datos personales sensibles salvo que el usuario voluntariamente los
        aporte en el contenido de su consulta.
      </P>

      <H2>2. Finalidad del tratamiento</H2>
      <P>Los datos recopilados se utilizan para:</P>
      <ul style={{ paddingLeft: 22, margin: "0 0 12px" }}>
        <li>Prestar el servicio solicitado por el usuario.</li>
        <li>Guardar las conversaciones del usuario para que estén disponibles en futuras sesiones.</li>
        <li>Mejorar la calidad y seguridad de la Plataforma.</li>
        <li>Cumplir obligaciones legales y atender requerimientos de autoridades competentes.</li>
      </ul>

      <H2>3. Servicios de terceros</H2>
      <P>
        PreLegal.cl utiliza proveedores tecnológicos para procesar texto e imágenes
        (modelos de lenguaje de OpenAI a través de infraestructura segura). Las consultas
        son enviadas a dichos proveedores únicamente con el propósito de generar la
        respuesta y se rigen por las políticas de privacidad de esos servicios. No
        vendemos ni cedemos datos personales a terceros con fines comerciales.
      </P>

      <H2>4. Conservación de los datos</H2>
      <P>
        Las conversaciones guardadas permanecen en la cuenta del usuario mientras este no
        las elimine. El usuario puede borrar cualquier conversación, junto con los
        documentos adjuntos asociados, desde la interfaz de la Plataforma.
      </P>
      <P>
        Los documentos que subiste para análisis puntual pueden ser eliminados
        automáticamente tras un periodo prudencial de inactividad.
      </P>

      <H2>5. Seguridad</H2>
      <P>
        Aplicamos medidas técnicas y organizativas razonables para proteger la
        información almacenada, incluyendo cifrado en tránsito y controles de acceso. No
        obstante, ningún sistema es absolutamente invulnerable; el usuario debe tomar las
        precauciones razonables sobre la información que comparte.
      </P>

      <H2>6. Derechos del titular de datos</H2>
      <P>
        Conforme a la ley chilena, el usuario puede ejercer los derechos de acceso,
        rectificación, cancelación y oposición respecto de sus datos personales. Para
        ello, puede contactarnos al correo indicado en la página principal de la
        Plataforma.
      </P>

      <H2>7. Cambios en esta política</H2>
      <P>
        Podremos actualizar esta Política de Privacidad para reflejar cambios legales u
        operativos. La versión vigente será siempre la publicada en esta página.
      </P>
    </LegalDocLayout>
  );
}
